/**
 * Typography configuration for the Employee Transfers application
 * Defines font families, sizes, weights, and styles
 */

// Font families
export const fontFamily = {
  primary: '"PizzaHutFont", "Open Sans", "Helvetica", "Arial", sans-serif',
  secondary: '"Open Sans", "Helvetica", "Arial", sans-serif',
  monospace: '"Roboto Mono", monospace',
};

// Font weights
export const fontWeight = {
  light: 300,
  regular: 400,
  semiBold: 600,
  bold: 700,
  medium: 500, // Add medium for compatibility with existing code
};

// Font sizes (in rem)
export const fontSize = {
  xs: 0.75,     // 12px
  sm: 0.875,    // 14px
  md: 1,        // 16px
  lg: 1.125,    // 18px
  xl: 1.75,     // 28px
  xxl: 2.25,    // 36px
};

// Line heights
export const lineHeight = {
  tight: 1.2,
  normal: 1.5,
};

// Letter spacing
export const letterSpacing = {
  normal: '0em',
  wide: '0.08em', // 8% letter spacing for all caps
};

// Typography variants
const typography = {
  fontFamily: fontFamily.secondary, // Default font is Open Sans
  
  // Headings
  h1: {
    fontFamily: fontFamily.primary, // Pizza Hut font
    fontWeight: fontWeight.regular,
    fontSize: `${fontSize.xxl}rem`, // 36px
    lineHeight: lineHeight.tight,
    letterSpacing: letterSpacing.normal,
  },
  h2: {
    fontFamily: fontFamily.secondary, // Open Sans
    fontWeight: fontWeight.light,
    fontSize: `${fontSize.xl}rem`, // 28px
    lineHeight: lineHeight.tight,
    letterSpacing: letterSpacing.normal,
  },
  h3: {
    fontFamily: fontFamily.secondary, // Open Sans
    fontWeight: fontWeight.regular,
    fontSize: `${fontSize.lg}rem`, // 18px
    lineHeight: lineHeight.tight,
    letterSpacing: letterSpacing.normal,
  },
  h4: {
    fontFamily: fontFamily.secondary,
    fontWeight: fontWeight.semiBold,
    fontSize: `${fontSize.md}rem`, // 16px
    lineHeight: lineHeight.tight,
    letterSpacing: letterSpacing.normal,
  },
  h5: {
    fontFamily: fontFamily.secondary,
    fontWeight: fontWeight.bold,
    fontSize: `${fontSize.sm}rem`, // 14px
    lineHeight: lineHeight.tight,
    letterSpacing: letterSpacing.normal,
  },
  h6: {
    fontFamily: fontFamily.secondary,
    fontWeight: fontWeight.bold,
    fontSize: `${fontSize.xs}rem`, // 12px
    lineHeight: lineHeight.tight,
    letterSpacing: letterSpacing.normal,
  },
  
  // Body text
  body1: {
    fontFamily: fontFamily.secondary,
    fontWeight: fontWeight.regular,
    fontSize: `${fontSize.sm}rem`, // 14px - Body Default
    lineHeight: lineHeight.normal,
    letterSpacing: letterSpacing.normal,
  },
  body2: {
    fontFamily: fontFamily.secondary,
    fontWeight: fontWeight.regular,
    fontSize: `${fontSize.xs}rem`, // 12px - Body Small
    lineHeight: lineHeight.normal,
    letterSpacing: letterSpacing.normal,
  },
  
  // Other variants
  subtitle1: {
    fontFamily: fontFamily.secondary,
    fontWeight: fontWeight.regular,
    fontSize: `${fontSize.sm}rem`, // 14px - Body Large
    lineHeight: lineHeight.normal,
    letterSpacing: letterSpacing.normal,
  },
  subtitle2: {
    fontFamily: fontFamily.secondary,
    fontWeight: fontWeight.bold,
    fontSize: `${fontSize.md}rem`, // 16px - Product Title
    lineHeight: lineHeight.normal,
    letterSpacing: letterSpacing.normal,
  },
  button: {
    fontFamily: fontFamily.secondary,
    fontWeight: fontWeight.regular,
    fontSize: `${fontSize.sm}rem`, // 14px
    lineHeight: lineHeight.normal,
    letterSpacing: letterSpacing.normal,
    textTransform: 'none' as const,
  },
  caption: {
    fontFamily: fontFamily.secondary,
    fontWeight: fontWeight.regular,
    fontSize: `${fontSize.xs}rem`, // 12px
    lineHeight: lineHeight.tight,
    letterSpacing: letterSpacing.wide,
    textTransform: 'uppercase' as const,
  },
  overline: {
    fontFamily: fontFamily.secondary,
    fontWeight: fontWeight.semiBold,
    fontSize: `${fontSize.sm}rem`, // 14px
    lineHeight: lineHeight.tight,
    letterSpacing: letterSpacing.wide,
    textTransform: 'uppercase' as const,
  },
};

export default typography;
